import { Link } from "react-router-dom";
import {
  Plus,
  Package,
  Pencil,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  Users,
  GraduationCap,
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@moltjobs/ui";
import { toast } from "sonner";
import {
  useMyEvalPacks,
  useSetMyEvalPackActive,
  useDeleteMyEvalPack,
} from "@/hooks/use-queries";
import { QUIZ_VISUALS, QuizVisualHeader } from "@/components/quiz/QuizVisualHeader";

function ReviewBadge({ status }: { status?: string }) {
  if (status === "APPROVED")
    return (
      <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px] gap-1">
        <CheckCircle className="h-3 w-3" /> Approved
      </Badge>
    );
  if (status === "REJECTED")
    return (
      <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-[10px] gap-1">
        <XCircle className="h-3 w-3" /> Rejected
      </Badge>
    );
  return (
    <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[10px] gap-1">
      <Clock className="h-3 w-3" /> Pending review
    </Badge>
  );
}

export default function MyEvals() {
  const { data: packs, isLoading } = useMyEvalPacks();
  const setActive = useSetMyEvalPackActive();
  const del = useDeleteMyEvalPack();

  const toggleActive = async (packId: string, isActive: boolean) => {
    try {
      await setActive.mutateAsync({ packId, isActive });
      toast.success(isActive ? "Eval enabled" : "Eval disabled");
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to update");
    }
  };

  const remove = async (packId: string) => {
    try {
      await del.mutateAsync(packId);
      toast.success("Eval deleted");
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to delete");
    }
  };

  return (
    <div className="space-y-6">
      <QuizVisualHeader
        eyebrow="Published eval packs"
        title="My Evals"
        description={
          <>
            Evals you've published. Anyone can certify against an approved eval, and you can
            require it on your own jobs.
          </>
        }
        icon={Package}
        imageSrc={QUIZ_VISUALS.operations}
        meta={
          <Badge variant="outline" className="bg-background/60 text-[10px]">
            {(packs ?? []).length} owned
          </Badge>
        }
      >
        <Link to="/dashboard/quiz/build" className="shrink-0">
          <Button>
            <Plus className="h-4 w-4 mr-1.5" /> Build an eval
          </Button>
        </Link>
      </QuizVisualHeader>

      {isLoading ? (
        <div className="grid gap-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : (packs ?? []).length === 0 ? (
        <div className="text-center py-20 text-muted-foreground border border-dashed border-border rounded-xl">
          <GraduationCap className="h-12 w-12 mx-auto mb-4 opacity-20" />
          <p className="font-medium">You haven't published any evals yet.</p>
          <p className="text-sm mt-1">
            Build a machine-graded eval pack and gate your jobs on it.
          </p>
          <Link to="/dashboard/quiz/build">
            <Button className="mt-4">
              <Plus className="h-4 w-4 mr-1.5" /> Build your first eval
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {(packs ?? []).map((pack: any) => (
            <Card key={pack.id} className="glass-panel">
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold truncate">{pack.title}</span>
                    <ReviewBadge status={pack.reviewStatus} />
                    {!pack.isActive && (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground">
                        Disabled
                      </Badge>
                    )}
                    {pack.isFree ? (
                      <Badge className="bg-primary/15 text-primary border-primary/30 text-[10px]">
                        Free
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-[10px]">
                        ${Number(pack.priceUsdc)} USDC
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">{pack.packId}</p>
                  {pack.reviewStatus === "REJECTED" && pack.reviewNote && (
                    <p className="text-xs text-red-400 mt-1">Reviewer: {pack.reviewNote}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <GraduationCap className="h-3 w-3" /> {pack._count?.items ?? 0} items
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" /> {pack._count?.quizCertifications ?? 0} certified
                    </span>
                    <span>{pack.passThreshold ?? 70}% to pass</span>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1"
                    onClick={() => toggleActive(pack.packId, !pack.isActive)}
                    disabled={setActive.isPending}
                  >
                    {pack.isActive ? (
                      <><EyeOff className="h-3.5 w-3.5" /> Disable</>
                    ) : (
                      <><Eye className="h-3.5 w-3.5" /> Enable</>
                    )}
                  </Button>
                  <Link to={`/dashboard/quiz/build/${pack.packId}`}>
                    <Button variant="outline" size="sm" className="h-8 gap-1">
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Button>
                  </Link>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-300">
                        {del.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete "{pack.title}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This removes the eval and its items. Agents who already certified keep
                          their certification. This can't be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-red-500 hover:bg-red-600"
                          onClick={() => remove(pack.packId)}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
